// src/pages/PatientsList.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  LinearProgress,
  Chip,
  Alert,
  Menu,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { patientService } from "../../services/patientService";
import { useAuth } from "../../contexts/AuthContext";

const PatientsList = () => {
  const navigate = useNavigate();
  const { canEditPatients } = useAuth();

  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Debounced search
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchPatients = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      setError("");

      try {

        // FIXED: Use proper patient service
        const response = await patientService.getPatients(
          searchQuery.trim(),
          page * rowsPerPage,
          rowsPerPage
        );


        if (Array.isArray(response)) {
          setPatients(response);
          // For pagination, we need total count - if not provided, estimate
          setTotalCount(
            response.length < rowsPerPage
              ? response.length
              : (page + 1) * rowsPerPage + 1
          );
        } else if (
          response &&
          response.patients &&
          Array.isArray(response.patients)
        ) {
          setPatients(response.patients);
          setTotalCount(response.total_matches || response.patients.length);
        } else {
          setPatients([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
        setError(`Failed to load patients: ${error.message}`);
        setPatients([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [searchQuery, page, rowsPerPage]
  );

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0); // Reset to first page on new search
      fetchPatients();
    }, 300);

    setSearchTimeout(timeout);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(0);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPatients(false);
  };

  const handleMenuOpen = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const handleViewPatient = (patient) => {
    navigate(`/patients/${patient.id}`);
    handleMenuClose();
  };

  const handleEditPatient = (patient) => {
    navigate(`/patients/${patient.id}/edit`);
    handleMenuClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatAddress = (patient) => {
    const parts = [];
    if (patient.address) parts.push(patient.address);
    if (patient.city) parts.push(patient.city);
    if (patient.state) parts.push(patient.state);
    if (patient.zip_code) parts.push(patient.zip_code);

    return parts.length > 0 ? parts.join(", ") : "No address";
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;

    try {
      const birth = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--;
      }

      return age > 0 ? age : null;
    } catch (error) {
      return null;
    }
  };

  const getPatientStatus = (patient) => {
    // You can implement logic to determine patient status based on recent orders, etc.
    return "Active"; // Placeholder
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <PersonIcon sx={{ mr: 1, fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4" fontWeight="bold">
            Patients
          </Typography>
          <Chip
            label={`${totalCount} total`}
            color="primary"
            variant="outlined"
            sx={{ ml: 2 }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={
              refreshing ? <LinearProgress size={20} /> : <RefreshIcon />
            }
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>

          {canEditPatients && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/patients/new")}
            >
              Add Patient
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Search and Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid  size={{ xs: 12, md: 8 }}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <TextField
              fullWidth
              placeholder="Search patients by name, address, phone, medical record number, or patient ID (e.g., P0V7)..."
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              💡 Tip: You can search by Patient ID (e.g., P0V7) for quick lookup
            </Typography>
          </Paper>
        </Grid>

        <Grid  size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent sx={{ py: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="h6" color="primary">
                    {searchQuery ? "Search Results" : "All Patients"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? `${patients.length} found${
                          searchQuery ? ` for "${searchQuery}"` : ""
                        }`
                      : `${totalCount} total patients`}
                  </Typography>
                </Box>
                <PersonIcon
                  color="primary"
                  sx={{ fontSize: 40, opacity: 0.7 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Patients Table */}
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Patient Info
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Date of Birth / Age
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Contact
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Address
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Status
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "white", fontWeight: "bold" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patients.length > 0 ? (
              patients.map((patient) => {
                const age = calculateAge(patient.date_of_birth);
                return (
                  <TableRow
                    key={patient.id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                        cursor: "pointer",
                      },
                    }}
                    onClick={() => navigate(`/patients/${patient.id}`)}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {patient.first_name} {patient.last_name}
                        </Typography>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{ mt: 0.5 }}
                        >
                          <Chip
                            label={`ID: ${patient.patient_id || "N/A"}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: "0.75rem", height: 20 }}
                          />
                          {patient.medical_record_number && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              MRN: {patient.medical_record_number}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(patient.date_of_birth)}
                        </Typography>
                        {age && (
                          <Typography variant="caption" color="text.secondary">
                            Age: {age}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        {patient.phone_number && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <PhoneIcon
                              sx={{
                                fontSize: 16,
                                mr: 0.5,
                                color: "text.secondary",
                              }}
                            />
                            <Typography variant="body2">
                              {patient.phone_number}
                            </Typography>
                          </Box>
                        )}
                        {patient.email && (
                          <Typography variant="caption" color="text.secondary">
                            {patient.email}
                          </Typography>
                        )}
                        {!patient.phone_number && !patient.email && (
                          <Typography variant="body2" color="text.secondary">
                            No contact info
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="flex-start">
                        <LocationIcon
                          sx={{
                            fontSize: 16,
                            mr: 0.5,
                            color: "text.secondary",
                            mt: 0.1,
                          }}
                        />
                        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                          {formatAddress(patient)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={getPatientStatus(patient)}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/patients/${patient.id}`);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        {canEditPatients && (
                          <Tooltip title="Edit Patient">
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/patients/${patient.id}/edit`);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, patient);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading patients...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : searchQuery ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Box>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      No patients found
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      No patients match your search for "{searchQuery}"
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleClearSearch}
                      startIcon={<ClearIcon />}
                    >
                      Clear Search
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Box>
                    <PersonIcon
                      sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      No patients yet
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      Start by adding your first patient to the system.
                    </Typography>
                    {canEditPatients && (
                      <Button
                        variant="contained"
                        onClick={() => navigate("/patients/new")}
                        startIcon={<AddIcon />}
                      >
                        Add First Patient
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {patients.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
          />
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleViewPatient(selectedPatient)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>

        {canEditPatients && (
          <MenuItem onClick={() => handleEditPatient(selectedPatient)}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Patient
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default PatientsList;
